import { ArrowRight, Users, Network, BarChart3, Target, TrendingUp } from "lucide-react";
import { Chapter, Reveal } from "@/components/Reveal";
import { GhostCTA } from "@/components/CTA";

const STEPS = [
  { t: "PERSONE", d: "Efficienza personale, team, ruoli e sviluppo del potenziale.", icon: Users },
  { t: "ORGANIZZAZIONE", d: "Processi, procedure, deleghe e struttura aziendale.", icon: Network },
  { t: "NUMERI", d: "Controllo di gestione, finanza e indicatori chiave.", icon: BarChart3 },
  { t: "MARKETING & VENDITE", d: "Strategie, posizionamento, acquisizione e vendite efficienti.", icon: Target },
  { t: "CRESCITA", d: "Risultati sostenibili e libertà imprenditoriale.", icon: TrendingUp },
];

const Method = () => (
  <section id="metodo" data-testid="method-section" className="relative overflow-hidden bg-anthracite py-28 text-white lg:py-40">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_15%_20%,rgba(212,175,55,0.06),transparent_50%)]" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12">
      <Chapter index="02" label="Il metodo" dark />
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Reveal>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
              Prima capire.
              <br />
              Poi intervenire.
            </h2>
          </Reveal>
        </div>
        <div className="flex flex-col justify-end lg:col-span-6">
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-white/60 md:text-lg">
              Ogni azienda è un sistema. Intervenire soltanto sul marketing, sulle vendite o sui
              numeri senza comprendere cosa sta succedendo nel resto dell'organizzazione rischia di
              risolvere un problema creandone un altro.
            </p>
            <p className="mt-5 text-base leading-relaxed text-white/60 md:text-lg">
              Per questo il mio lavoro parte sempre da una visione complessiva.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mt-20 grid gap-4 sm:grid-cols-2 lg:mt-28 lg:grid-cols-5" data-testid="method-path">
        {STEPS.map((step, i) => (
          <Reveal key={step.t} delay={i * 0.08}>
            <article
              data-testid={`method-step-${i + 1}`}
              className="gold-glow-hover group relative h-full border border-white/10 bg-[#0C0C0C] p-7 hover:border-accent/50"
            >
              <step.icon className="h-6 w-6 text-accent" aria-hidden="true" />
              <p className="mt-7 font-mono text-[10px] tracking-[0.3em] text-accent/70">FASE 0{i + 1}</p>
              <h3 className="mt-3 font-display text-base font-bold tracking-tight lg:text-lg">{step.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/50">{step.d}</p>
              {i < STEPS.length - 1 && (
                <ArrowRight
                  className="absolute -right-3.5 top-1/2 hidden h-4 w-4 -translate-y-1/2 text-accent/40 lg:block"
                  aria-hidden="true"
                />
              )}
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15} className="mt-14">
        <p
          className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40"
          data-testid="method-outcome"
        >
          <span>Azienda più forte</span>
          <span className="text-accent" aria-hidden="true">+</span>
          <span>Persone più autonome</span>
          <span className="text-accent" aria-hidden="true">+</span>
          <span>Numeri più sani</span>
          <span className="text-accent" aria-hidden="true">+</span>
          <span className="text-accent">Imprenditore più libero</span>
        </p>
      </Reveal>

      <Reveal delay={0.2} className="mt-16">
        <GhostCTA to="/#consulenza" dark testid="method-cta">
          Scopri come lavoro
        </GhostCTA>
      </Reveal>
    </div>
  </section>
);

export default Method;
